import { Stack, Typography } from "@mui/material";

import { CoAppLightTooltip } from "../../styled/global.styled";
import { ChipList, GroupChip, TagChip } from "../TagChip";

/**
 * CoAppDataGridListCell — renders a comma-separated list of related items
 * (Roles, Groups, Permissions, Tags, Users) inside a data grid cell.
 *
 * Now renders as soft tinted pill chips (matching the prototype + marketing
 * brand) with overflow handled via a "+N more" chip + tooltip listing the
 * full set. Resource name drives the chip flavor:
 *   - "Tags" / "Permissions" → hashed-color TagChip
 *   - everything else (Groups, Roles, Users) → neutral GroupChip
 */
const MAX_VISIBLE_CHIPS = 4;

const formatLabel = (item, isUser) => {
	if (isUser && item && typeof item === "object") {
		if (item.firstname && item.lastname) return `${item.firstname} ${item.lastname}`;
		return item.email || "";
	}
	if (item && typeof item === "object") return item.name ?? item.label ?? "";
	return String(item ?? "");
};

export default function CoAppDataGridListCell(props) {
	const {
		items = [], resource, isUser = false,
		isCoAppSuperAdmin = false
	} = props;

	const labels = (items || []).map((item) => formatLabel(item, isUser)).filter(Boolean);
	const tooltipTitle = labels.join(", ");

	if (labels.length === 0) {
		return (
			<Stack
				direction="row"
				sx={{ height: "100%", alignItems: "center" }}
			>
				<Typography
					component="span"
					sx={{
						fontSize: 12,
						fontStyle: "italic",
						color: isCoAppSuperAdmin ? "rgba(13, 31, 48, 0.30)" : "rgba(13, 31, 48, 0.40)",
						letterSpacing: "0.01em"
					}}
				>
					No {resource}
				</Typography>
			</Stack>
		);
	}

	// Tags / Permissions get color-hashed chips; everything else uses neutral.
	const isTagFlavor = resource === "Tags" || resource === "Permissions";
	const ChipComponent = isTagFlavor ? TagChip : GroupChip;

	const visible = labels.slice(0, MAX_VISIBLE_CHIPS);
	const overflow = labels.length - visible.length;

	const cell = (
		<Stack
			direction="row"
			sx={{
				height: "100%",
				width: "100%",
				py: 0.5,
				alignItems: "center",
				gap: 0.5,
				flexWrap: "wrap",
			}}
		>
			{visible.map((label, i) => (
				<ChipComponent key={`${label}-${i}`} label={label} />
			))}
			{overflow > 0 && (
				<Typography
					component="span"
					sx={{
						fontSize: 11,
						fontWeight: 600,
						color: "rgba(13, 31, 48, 0.55)",
						letterSpacing: "0.02em",
						px: 0.75,
						py: 0.25,
						borderRadius: 999,
						background: "rgba(13, 31, 48, 0.06)"
					}}
				>
					+{overflow}
				</Typography>
			)}
		</Stack>
	);

	return overflow > 0 ? (
		<CoAppLightTooltip title={tooltipTitle} placement="top">
			<div>{cell}</div>
		</CoAppLightTooltip>
	) : cell;
}